import { useEffect, useState } from "react";

interface UseProgressiveImageProps {
  lowResUrl?: string;
  highResUrl?: string;
}

interface UseProgressiveImageResult {
  imgSrc: string | undefined;
  isHighResLoaded: boolean;
  isLoading: boolean;
}

export function useProgressiveImage({ 
  lowResUrl,
  highResUrl,
}: UseProgressiveImageProps): UseProgressiveImageResult {
  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined);
  const [isHighResLoaded, setIsHighResLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Reset state when URLs change
    setImgSrc(undefined);
    setIsHighResLoaded(false);
    setIsLoading(false);

    // If no URLs provided, exit early
    if (!lowResUrl && !highResUrl) {
      return;
    }

    setIsLoading(true);

    // Start with low-res (or high-res directly if no low-res)
    const initialSrc = lowResUrl || highResUrl;
    setImgSrc(initialSrc);
    
    // If both URLs are the same or only one URL exists, consider it fully loaded
    if (!lowResUrl || !highResUrl || lowResUrl === highResUrl) {
      setIsHighResLoaded(true);
      setIsLoading(false);
      return;
    }

    // If both exist and differ, preload high-res then swap
    if (lowResUrl && highResUrl && lowResUrl !== highResUrl) {
      const img = new Image();
      img.src = highResUrl;
      img.onload = () => {
        setImgSrc(highResUrl);
        setIsHighResLoaded(true);
        setIsLoading(false);
      };
      img.onerror = () => {
        // If high-res fails to load, keep the low-res
        setIsLoading(false);
      };
    }
  }, [lowResUrl, highResUrl]);

  return {
    imgSrc,
    isHighResLoaded,
    isLoading
  };
}
